import { useEffect, useRef, useState } from "react";
import { Info } from "@mui/icons-material";
import ReactDOM from 'react-dom';

let activePersistentHint = null;

const HintHelper = ({ message, className, messageStyle = "" }) => {
  const [showHint, setShowHint] = useState(false);
  const [isPersistent, setIsPersistent] = useState(false);
  const hintRef = useRef(null);
  const modalRef = useRef(null);
  const hintId = useRef(Math.random().toString(36).substring(2, 9));
  const [maxHeight, setMaxHeight] = useState("70vh");
  const [position, setPosition] = useState({ top: 0, left: 0 });
  
  const portalRef = useRef(document.createElement('div'));

  useEffect(() => {
    const portalElement = portalRef.current;
    document.body.appendChild(portalElement);
    
    const originalOverflowX = document.body.style.overflowX;
    
    return () => {
      document.body.removeChild(portalElement);
      document.body.style.overflowX = originalOverflowX;
    };
  }, []);

  const calculatePosition = () => {
    if (!hintRef.current) return;
    
    const rect = hintRef.current.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    
    const hintWidth = window.innerWidth < 768 ? Math.min(256, window.innerWidth - 20) : 256;
    const margin = 10;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let left = rect.right + scrollX + 5;
    let top = rect.top + scrollY;
    
    if (left + hintWidth + margin > viewportWidth + scrollX) {
      left = rect.left + scrollX - hintWidth - 5;
      
      if (left < scrollX + margin) {
        left = scrollX + margin;
      }
    }
    
    const estimatedHintHeight = 150; 
    if (top + estimatedHintHeight > viewportHeight + scrollY) {
      top = rect.top + scrollY - estimatedHintHeight - 5;
      
      if (top < scrollY + margin) {
        top = scrollY + margin;
      }
    }
    
    return {
      top: Math.max(scrollY + margin, top),
      left: Math.max(scrollX + margin, Math.min(left, viewportWidth + scrollX - hintWidth - margin)),
    };
  };

  const renderMessage = () => {
    if (typeof message === "string") {
      return <div dangerouslySetInnerHTML={{ __html: message }} />;
    }
    
    if (Array.isArray(message)) {
      return (
        <div>
          {message.map((item, index) => {
            if (typeof item === "string") {
              return (
                <div 
                  key={index} 
                  dangerouslySetInnerHTML={{ __html: item }}
                  className={index > 0 ? "mt-2" : ""}
                />
              );
            }
            return (
              <div key={index} className={index > 0 ? "mt-2" : ""}>
                {item}
              </div>
            );
          })}
        </div>
      );
    }
    
    return message;
  };

  const handleModalMouseDown = (e) => {
    e.stopPropagation();
  };

  const handleClick = () => {
    if (activePersistentHint && activePersistentHint !== hintId.current) {
      activePersistentHint = null;
    }

    if (isPersistent) {
      setIsPersistent(false);
      setShowHint(false);
      activePersistentHint = null;
    } else {
      const pos = calculatePosition();
      if (pos) {
        setPosition({
          top: pos.top,
          left: pos.left
        });
      }
      setIsPersistent(true);
      setShowHint(true);
      activePersistentHint = hintId.current;
    }
  };

  const handleMouseEnter = () => {
    if (activePersistentHint && activePersistentHint !== hintId.current) {
      return;
    }

    if (window.innerWidth > 768) {
      const pos = calculatePosition();
      if (pos) {
        setPosition({
          top: pos.top,
          left: pos.left
        });
      }
      setShowHint(true);
    }
  };

  const handleMouseLeave = () => {
    if (isPersistent) return;

    if (window.innerWidth > 768) {
      setShowHint(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hintRef.current && !hintRef.current.contains(event.target) &&
          modalRef.current && !modalRef.current.contains(event.target)) {
        setShowHint(false);
        setIsPersistent(false);
        if (activePersistentHint === hintId.current) {
          activePersistentHint = null;
        }
      }
    };

    if (showHint) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHint]);

  useEffect(() => {
    const calculateMaxHeight = () => {
      const vh = window.innerHeight * 0.7;
      setMaxHeight(`${Math.min(vh, 190)}px`);
    };
    calculateMaxHeight();
    window.addEventListener("resize", calculateMaxHeight);

    return () => {
      const hintAtual = hintId;
      if (activePersistentHint === hintAtual) {
        activePersistentHint = null;
      }
    };
  }, []);

  useEffect(() => {
    if (showHint) {
      const handleScrollResize = () => {
        const pos = calculatePosition();
        if (pos) {
          setPosition({
            top: pos.top,
            left: pos.left
          });
        }
      };
      
      const debouncedHandleResize = () => {
        clearTimeout(window.resizeTimeout);
        window.resizeTimeout = setTimeout(handleScrollResize, 100);
      };
      
      window.addEventListener('scroll', handleScrollResize);
      window.addEventListener('resize', debouncedHandleResize);
      window.addEventListener('orientationchange', handleScrollResize);
      
      return () => {
        window.removeEventListener('scroll', handleScrollResize);
        window.removeEventListener('resize', debouncedHandleResize);
        window.removeEventListener('orientationchange', handleScrollResize);
        clearTimeout(window.resizeTimeout);
      };
    }
  }, [showHint]);

  return (
    <div
      className={`absolute inline-block ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={hintRef}
    >
      <Info
        sx={{ fontSize: 16 }}
        className={`text-gray-400 hover:text-gray-600 cursor-pointer transition-colors ${
          isPersistent ? "text-gray-600" : ""
        }`}
        onClick={handleClick}
      />
      {showHint && ReactDOM.createPortal(
        <div
          ref={modalRef}
          onMouseDown={handleModalMouseDown}
          className={`absolute p-3 text-sm bg-gray-50 border-4 rounded-md border-emerald-600 text-gray-800 shadow-2xl z-[9999] overflow-auto select-text ${messageStyle}`}
          style={{ 
            maxHeight,
            top: position.top,
            left: position.left,
            width: window.innerWidth < 768 ? `${Math.min(256, window.innerWidth - 20)}px` : '256px',
            maxWidth: 'calc(100vw - 20px)',
            boxSizing: 'border-box',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            userSelect: 'text',
            WebkitUserSelect: 'text',
            MozUserSelect: 'text',
            msUserSelect: 'text'
          }}
        >
          {renderMessage()}
        </div>,
        portalRef.current
      )}
    </div>
  );
};

export default HintHelper;